import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Tracks whether the current admin has acknowledged the sensitive-data
 * feature disclaimer. The acknowledgment is stored server-side in
 * `business_info.sensitive_data_disclaimer_ack` and audit-logged.
 *
 * Returns:
 *   {
 *     loading,         // true until first fetch resolves
 *     acknowledged,    // bool — has someone acked the current version
 *     ackedAt,         // ISO timestamp string or null
 *     ackedBy,         // email string or null
 *     acknowledge(),   // async — POSTs the ack as the current admin
 *     refresh(),       // re-check
 *   }
 *
 * Designed to be called by feature gates that should show the disclaimer
 * the first time an admin attempts to enable a sensitive feature (insurance
 * receipts, intake forms, charting). Once acknowledged it stays acknowledged
 * until the disclaimer version is bumped server-side.
 */
export default function useSensitiveDataAck() {
  const [state, setState] = useState({
    loading: true,
    acknowledged: false,
    ackedAt: null,
    ackedBy: null,
  });

  const refresh = useCallback(async () => {
    try {
      const token = sessionStorage.getItem('token');
      const res = await axios.get(`${API_URL}/sensitive-data-ack`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setState({
        loading: false,
        acknowledged: !!res.data.acknowledged,
        ackedAt: res.data.ackedAt || null,
        ackedBy: res.data.ackedBy || null,
      });
    } catch (e) {
      // Non-admin or network failure: assume acknowledged so we don't
      // block legitimate work. Hard gate happens server-side in policy
      // (admin-only routes). The disclaimer is a UX nudge, not a security
      // boundary.
      setState({ loading: false, acknowledged: true, ackedAt: null, ackedBy: null });
    }
  }, []);

  const acknowledge = useCallback(async () => {
    const token = sessionStorage.getItem('token');
    const res = await axios.post(`${API_URL}/sensitive-data-ack`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setState({
      loading: false,
      acknowledged: !!res.data.acknowledged,
      ackedAt: res.data.ackedAt || null,
      ackedBy: res.data.ackedBy || null,
    });
    return res.data;
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return { ...state, acknowledge, refresh };
}
